import { openDB } from 'idb'

const STORE = 'chunks'

export class AudioBuffer {
  constructor(dbName = 'clarity-audio') { this.dbName = dbName; this.db = null }

  async open() {
    this.db = await openDB(this.dbName, 1, {
      upgrade(db) {
        const s = db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true })
        s.createIndex('by_session_seq', ['sessionId', 'seq'])
        s.createIndex('by_session_status', ['sessionId', 'status'])
      },
    })
  }

  async put({ sessionId, seq, data }) {
    // `data` is an ArrayBuffer (raw audio bytes), not a Blob. iOS Safari's
    // IndexedDB silently fails/hangs storing Blob values — ArrayBuffers are
    // reliable across all browsers.
    await this.db.add(STORE, { sessionId, seq, data, status: 'pending', ts: Date.now() })
  }

  async takePending(sessionId) {
    const tx = this.db.transaction(STORE, 'readwrite')
    const idx = tx.store.index('by_session_seq')
    let cursor = await idx.openCursor(IDBKeyRange.bound([sessionId, 0], [sessionId, Number.MAX_SAFE_INTEGER]))
    while (cursor) {
      if (cursor.value.status === 'pending') {
        const updated = { ...cursor.value, status: 'inflight' }
        await cursor.update(updated)
        await tx.done
        return updated
      }
      cursor = await cursor.continue()
    }
    await tx.done
    return null
  }

  async ackUpTo(sessionId, seqInclusive) {
    // Collect all items that need deletion first
    const all = await this.allBySession(sessionId)
    const toDeleteIds = all
      .filter(c => c.seq <= seqInclusive && (c.status === 'inflight' || c.status === 'pending'))
      .map(c => c.id)

    // Now delete them in a transaction
    if (toDeleteIds.length > 0) {
      const tx = this.db.transaction(STORE, 'readwrite')
      for (const id of toDeleteIds) {
        tx.store.delete(id)
      }
      await tx.done
    }
  }

  async reopenInflight(sessionId) {
    const tx = this.db.transaction(STORE, 'readwrite')
    const idx = tx.store.index('by_session_seq')
    let cursor = await idx.openCursor(IDBKeyRange.bound([sessionId, 0], [sessionId, Number.MAX_SAFE_INTEGER]))
    while (cursor) {
      if (cursor.value.status === 'inflight') {
        await cursor.update({ ...cursor.value, status: 'pending' })
      }
      cursor = await cursor.continue()
    }
    await tx.done
  }

  async allBySession(sessionId) {
    const tx = this.db.transaction(STORE, 'readonly')
    const idx = tx.store.index('by_session_seq')
    const out = []
    let cursor = await idx.openCursor(IDBKeyRange.bound([sessionId, 0], [sessionId, Number.MAX_SAFE_INTEGER]))
    while (cursor) { out.push(cursor.value); cursor = await cursor.continue() }
    await tx.done
    return out
  }

  async evictOldest(sessionId, cap) {
    const all = await this.allBySession(sessionId)
    if (all.length <= cap) return
    const sorted = all.sort((a, b) => a.seq - b.seq)
    const toDelete = sorted.slice(0, all.length - cap)
    const tx = this.db.transaction(STORE, 'readwrite')
    for (const c of toDelete) await tx.store.delete(c.id)
    await tx.done
  }

  async drop(sessionId) {
    const tx = this.db.transaction(STORE, 'readwrite')
    const idx = tx.store.index('by_session_seq')
    let cursor = await idx.openCursor(IDBKeyRange.bound([sessionId, 0], [sessionId, Number.MAX_SAFE_INTEGER]))
    while (cursor) { await cursor.delete(); cursor = await cursor.continue() }
    await tx.done
  }
}
